import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo
} from "react";
import Hls from "hls.js";
import { useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import ArrowBack from "@material-ui/icons/ArrowBack";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import { useApiGet } from "../utils/useApi";
import api from "../utils/api";
import styles from "./SyncedVideos.module.css";

const SEGMENT_COLORS = [
  "rgba(79,195,247,0.4)",
  "rgba(129,199,132,0.4)",
  "rgba(255,183,77,0.4)",
  "rgba(244,143,177,0.4)",
  "rgba(206,147,216,0.4)",
  "rgba(128,203,196,0.4)"
];

const PRIORITY_COLORS = {
  high: "#e53935",
  medium: "#fb8c00",
  low: "#43a047"
};

const ACTIVE_SEGMENT_EVENT_COLOR = "#fdd835";

function formatTime(seconds) {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatRecordingTime(seconds) {
  if (!isFinite(seconds) || seconds < 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s
      .toString()
      .padStart(2, "0")}`;
  }
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function getUploadUrl(writeUrl, fileName) {
  const encodedName = encodeURIComponent(fileName);

  if (writeUrl.includes("{filename}")) {
    return writeUrl.replace("{filename}", encodedName);
  }

  const queryIndex = writeUrl.indexOf("?");
  const hasQuery = queryIndex >= 0;
  const baseUrl = hasQuery ? writeUrl.substring(0, queryIndex) : writeUrl;
  const query = hasQuery ? writeUrl.substring(queryIndex) : "";
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${normalizedBase}${encodedName}${query}`;
}

function uploadBlobWithProgress(url, blob, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader(
      "Content-Type",
      contentType || "application/octet-stream"
    );

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`Blob upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error("Blob upload failed"));
    xhr.send(blob);
  });
}

async function computeSha256Hex(blob) {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error("SHA-256 hashing is not supported in this browser");
  }

  const buffer = await blob.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function SyncedVideos({ session, videos, onBack }) {
  const history = useHistory();
  const videoRefs = useRef([]);
  const hlsRefs = useRef([]);
  const rafRef = useRef(null);
  const videoOffsetsRef = useRef([]);
  const sessionTimeRef = useRef(0); // source-of-truth for session position
  const lastRafTimeRef = useRef(null); // wall-clock timestamp of previous RAF tick
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const recordingUrlRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const recordingBeginTimeRef = useRef(0);
  const eventRowRefs = useRef({});
  const lastAutoScrolledEventIdRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [sessionTimeSec, setSessionTimeSec] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsedSec, setRecordingElapsedSec] = useState(0);
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);
  const [uploadProgressPct, setUploadProgressPct] = useState(0);

  const [resultState] = useApiGet(
    `Result/All/${session.resultId}`,
    null,
    history
  );
  const resultData = resultState.data;

  // Session timeline bounds (microseconds → seconds)
  const beginSessionTime = resultData?.details?.beginSessionTime ?? 0;
  const endSessionTime = resultData?.details?.endSessionTime ?? 0;
  const sessionDuration =
    endSessionTime > beginSessionTime
      ? (endSessionTime - beginSessionTime) / 1000000
      : 0;

  const duration = sessionDuration || videoDuration;
  const resultDetailsId =
    resultData?.details?.id || session.resultDetailsId || 0;
  const inferredFileTypeId =
    resultData?.resultFiles?.find(f => f.fileTypeId)?.fileTypeId || 0;

  // Match result files (video files only) to the videos array by index
  const videoFiles = useMemo(() => {
    if (!resultData) return [];
    return resultData.resultFiles
      .filter(f => f.azureMSAssetName)
      .sort((a, b) => a.beginTime - b.beginTime);
  }, [resultData]);

  // Offset in seconds from session start for each video
  const videoOffsets = useMemo(
    () =>
      videos.map((_, i) => {
        const file = videoFiles[i];
        if (!file || !beginSessionTime) return 0;
        return (file.beginTime - beginSessionTime) / 1000000;
      }),
    [videos, videoFiles, beginSessionTime]
  );

  // Keep a ref so RAF and callbacks always see the latest values
  videoOffsetsRef.current = videoOffsets;

  const segments = useMemo(() => {
    if (!resultData) return [];
    return (resultData.segments || []).map(s => ({
      ...s,
      startSec: (s.startTime - beginSessionTime) / 1000000,
      endSec: (s.endTime - beginSessionTime) / 1000000
    }));
  }, [resultData, beginSessionTime]);

  const activeSegmentIds = useMemo(() => {
    const active = new Set();
    segments.forEach(seg => {
      if (sessionTimeSec >= seg.startSec && sessionTimeSec <= seg.endSec) {
        active.add(seg.id);
      }
    });
    return active;
  }, [segments, sessionTimeSec]);

  const isEventInActiveSegment = useCallback(
    evt =>
      segments.some(
        seg =>
          activeSegmentIds.has(seg.id) &&
          evt.timeSec >= seg.startSec &&
          evt.timeSec <= seg.endSec
      ),
    [segments, activeSegmentIds]
  );

  const events = useMemo(() => {
    if (!resultData) return [];
    return (resultData.events || []).map(e => ({
      ...e,
      timeSec: (e.time - beginSessionTime) / 1000000
    }));
  }, [resultData, beginSessionTime]);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.timeSec - b.timeSec),
    [events]
  );

  const getSource = video => {
    const urls = video.urls || [];
    const hls = urls.find(url => url.includes(".m3u8"));
    if (hls)
      return {
        src: hls,
        type: "application/vnd.apple.mpegurl",
        sasToken: video.sasToken
      };
    const mp4 = urls.find(url => url.includes(".mp4"));
    if (mp4) return { src: mp4, type: "video/mp4" };
    return null;
  };

  // Load each video source
  useEffect(() => {
    videos.forEach((video, index) => {
      const el = videoRefs.current[index];
      if (!el) return;
      const source = getSource(video);
      if (!source) return;
      const { src: url, type, sasToken } = source;
      const isHls = url.includes(".m3u8");

      if (isHls && Hls.isSupported()) {
        const hlsConfig = sasToken
          ? {
              xhrSetup: (xhr, reqUrl) => {
                if (!reqUrl.includes("?")) reqUrl += sasToken;
                else if (!reqUrl.includes("sig="))
                  reqUrl += "&" + sasToken.substring(1);
                xhr.open("GET", reqUrl, true);
              }
            }
          : {};
        const hls = new Hls(hlsConfig);
        hlsRefs.current[index] = hls;
        hls.loadSource(url);
        hls.attachMedia(el);
      } else {
        while (el.firstChild) el.removeChild(el.firstChild);
        const sourceEl = document.createElement("source");
        sourceEl.src = url;
        if (type) sourceEl.type = type;
        el.appendChild(sourceEl);
        el.load();
      }
    });

    return () => {
      hlsRefs.current.forEach(hls => {
        if (hls) hls.destroy();
      });
      hlsRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  // Track duration + play/pause state from primary video
  useEffect(() => {
    const primary = videoRefs.current[0];
    if (!primary) return;
    const onDuration = () => {
      if (isFinite(primary.duration)) setVideoDuration(primary.duration);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    primary.addEventListener("durationchange", onDuration);
    primary.addEventListener("play", onPlay);
    primary.addEventListener("pause", onPause);
    return () => {
      primary.removeEventListener("durationchange", onDuration);
      primary.removeEventListener("play", onPlay);
      primary.removeEventListener("pause", onPause);
    };
  }, []);

  // RAF loop: advance session time and keep all videos in sync
  useEffect(() => {
    if (!isPlaying) {
      cancelAnimationFrame(rafRef.current);
      lastRafTimeRef.current = null;
      return;
    }
    const tick = now => {
      const delta =
        lastRafTimeRef.current !== null
          ? (now - lastRafTimeRef.current) / 1000
          : 0;
      lastRafTimeRef.current = now;

      // Trust the primary video's position only when it is close to where we
      // expect — guards against the transient jump-to-0 that occurs right after
      // a seek + play on HLS streams.
      const primary = videoRefs.current[0];

      // If the primary video is paused, stop driving session time forward.
      if (primary && primary.paused) {
        setIsPlaying(false);
        lastRafTimeRef.current = null;
        return;
      }

      let t;
      if (primary && isFinite(primary.currentTime)) {
        const primaryT =
          primary.currentTime + (videoOffsetsRef.current[0] || 0);
        t =
          Math.abs(primaryT - sessionTimeRef.current) < 2
            ? primaryT
            : sessionTimeRef.current + delta;
      } else {
        t = sessionTimeRef.current + delta;
      }

      sessionTimeRef.current = t;
      setSessionTimeSec(t);

      const offsets = videoOffsetsRef.current;
      videoRefs.current.forEach((el, i) => {
        if (!el || i === 0) return;
        const target = t - (offsets[i] || 0);
        if (target < 0) {
          if (el.currentTime !== 0) el.currentTime = 0;
        } else {
          const clamped = isFinite(el.duration)
            ? Math.min(target, el.duration)
            : target;
          if (Math.abs(el.currentTime - clamped) > 0.5)
            el.currentTime = clamped;
        }
      });

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastRafTimeRef.current = null;
    };
  }, [isPlaying]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      videoRefs.current.forEach(v => v && v.pause());
      setIsPlaying(false);
    } else {
      const offsets = videoOffsetsRef.current;
      const currentT = sessionTimeRef.current;
      videoRefs.current.forEach((v, i) => {
        if (!v) return;
        if (currentT >= (offsets[i] || 0)) v.play().catch(() => {});
      });
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleSliderChange = useCallback(
    e => {
      const time = parseFloat(e.target.value);
      sessionTimeRef.current = time;
      setSessionTimeSec(time);
      const offsets = videoOffsetsRef.current;

      const seekVideo = (el, target) => {
        if (target < 0) {
          if (typeof el.fastSeek === "function") el.fastSeek(0);
          else el.currentTime = 0;
          return;
        }

        const clamped = isFinite(el.duration)
          ? Math.min(target, el.duration)
          : target;
        if (typeof el.fastSeek === "function") el.fastSeek(clamped);
        else el.currentTime = clamped;
      };

      videoRefs.current.forEach((el, i) => {
        if (!el) return;
        const target = time - (offsets[i] || 0);
        seekVideo(el, target);

        // Keep media paused while scrubbing paused timeline.
        if (!isPlaying && !el.paused) {
          el.pause();
        }
      });
    },
    [isPlaying]
  );

  const cleanupRecordingResources = useCallback(() => {
    if (recordingUrlRef.current) {
      URL.revokeObjectURL(recordingUrlRef.current);
      recordingUrlRef.current = null;
    }

    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach(track => track.stop());
      displayStreamRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    recorderRef.current = null;
    recordedChunksRef.current = [];
    recordingStartedAtRef.current = null;
  }, []);

  const triggerRecordingDownload = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob);
    recordingUrlRef.current = url;

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, []);

  const uploadRecordingToBackend = useCallback(
    async (blob, filename, contentType) => {
      const fileHash = await computeSha256Hex(blob);
      const fileTypes = await api.get("Result/FileTypes", null, history);
      const interviewVideoType = (fileTypes || []).find(
        item => (item.fileType || "").toLowerCase() === "interview video"
      );
      const uploadFileTypeId =
        interviewVideoType?.id || inferredFileTypeId || 0;

      // 1) Register the new result file entry
      const newResultFile = {
        Id: 0,
        ResultDetailsId: resultDetailsId,
        FilePath: filename,
        TimeUploaded: "2010-01-01T00:00:00Z", // Pre-2019 so SP will update
        Hash: fileHash,
        FileTypeId: uploadFileTypeId,
        AzureMSAssetName: "",
        BeginTime: recordingBeginTimeRef.current
      };

      await api.post("Result/ResultFiles", [newResultFile], history);

      // 2) Resolve created resultFileId by filename
      const resultFiles = await api.get(
        `Result/ResultFiles/${session.resultId}`,
        null,
        history
      );

      const matches = (resultFiles || []).filter(file => {
        const serverName = (file.filePath || "")
          .split(/[/\\]/)
          .pop()
          .toLowerCase();
        return serverName === filename.toLowerCase();
      });

      const created = matches.sort((a, b) => (b.id || 0) - (a.id || 0))[0];
      if (!created || !created.id) {
        throw new Error("Unable to resolve uploaded ResultFile ID");
      }

      // 3) Get writable URL and upload to blob storage with progress
      const writeUrl = await api.get(
        `Result/WriteUrl/${session.resultId}`,
        null,
        history
      );
      const targetUrl = getUploadUrl(String(writeUrl || ""), filename);
      await uploadBlobWithProgress(targetUrl, blob, contentType, pct => {
        setUploadProgressPct(pct);
      });

      // 4) Notify backend that upload completed
      const uploadedResult = await api.put(
        `Result/ResultFileUploaded2/${created.id}`,
        null,
        history
      );

      if (!uploadedResult || uploadedResult.updated !== true) {
        throw new Error(
          `Backend did not confirm upload completion. Response: ${JSON.stringify(
            uploadedResult
          )}`
        );
      }

      if (
        uploadedResult.resultDetailsId &&
        resultDetailsId &&
        uploadedResult.resultDetailsId !== resultDetailsId
      ) {
        throw new Error(
          "Upload completion response returned a mismatched ResultDetailsId"
        );
      }
    },
    [history, inferredFileTypeId, resultDetailsId, session.resultId]
  );

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      setIsRecording(false);
      return;
    }

    cleanupRecordingResources();
    setIsRecording(false);
  }, [cleanupRecordingResources]);

  const startRecording = useCallback(async () => {
    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getDisplayMedia ||
      !navigator.mediaDevices.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      window.alert("Recording is not supported in this browser.");
      return;
    }

    try {
      let displayStream;
      try {
        // Best-effort hints to reduce chooser complexity in supporting browsers.
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            frameRate: 30,
            displaySurface: "window"
          },
          audio: true,
          preferCurrentTab: true,
          surfaceSwitching: "exclude",
          monitorTypeSurfaces: "exclude"
        });
      } catch (captureError) {
        // Fallback for browsers that do not support the above hint set.
        if (
          captureError &&
          (captureError.name === "TypeError" ||
            captureError.name === "OverconstrainedError")
        ) {
          displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: { frameRate: 30 },
            audio: true
          });
        } else {
          throw captureError;
        }
      }

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      displayStreamRef.current = displayStream;
      micStreamRef.current = micStream;

      // Mix display audio + mic audio into a single track via Web Audio API.
      // MediaRecorder only records one audio track, so we must merge first.
      const mixCtx = new AudioContext();
      const mixDest = mixCtx.createMediaStreamDestination();
      if (displayStream.getAudioTracks().length > 0) {
        mixCtx.createMediaStreamSource(displayStream).connect(mixDest);
      }
      mixCtx.createMediaStreamSource(micStream).connect(mixDest);

      const combinedStream = new MediaStream([
        ...displayStream.getVideoTracks(),
        ...mixDest.stream.getAudioTracks()
      ]);

      let mimeType = "video/webm;codecs=vp9,opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/webm;codecs=vp8,opus";
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/webm";
      }

      const recorder = new MediaRecorder(combinedStream, { mimeType });
      recorderRef.current = recorder;
      recordedChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();
      recordingBeginTimeRef.current = Math.round(
        beginSessionTime + sessionTimeRef.current * 1000000
      );
      setRecordingElapsedSec(0);

      recorder.ondataavailable = evt => {
        if (evt.data && evt.data.size > 0) {
          recordedChunksRef.current.push(evt.data);
        }
      };

      recorder.onstop = async () => {
        const chunks = recordedChunksRef.current;
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: mimeType });
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          const filename = `synced-session-${session.resultId}-${timestamp}.webm`;

          try {
            setIsUploadingRecording(true);
            setUploadProgressPct(0);
            await uploadRecordingToBackend(blob, filename, mimeType);
            setUploadProgressPct(100);
          } catch (uploadErr) {
            // Keep local fallback so the recording is never lost.
            triggerRecordingDownload(blob, filename);
            window.alert(
              "Upload failed, recording was downloaded locally instead."
            );
          } finally {
            setIsUploadingRecording(false);
          }
        }

        cleanupRecordingResources();
        setIsRecording(false);
      };

      const endTrack = displayStream.getVideoTracks()[0];
      if (endTrack) {
        endTrack.addEventListener("ended", () => {
          const activeRecorder = recorderRef.current;
          if (activeRecorder && activeRecorder.state !== "inactive") {
            activeRecorder.stop();
          }
        });
      }

      recorder.start(1000);
      setIsRecording(true);

      // Monitor mic audio levels to verify capture is working
      const audioCtx = new AudioContext();
      await audioCtx.resume();
      const micSource = audioCtx.createMediaStreamSource(micStream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      micSource.connect(analyser);
      const monitorAudio = () => {
        if (!recorderRef.current || recorderRef.current.state !== "recording") {
          audioCtx.close();
          mixCtx.close();
          return;
        }
        analyser.getByteFrequencyData(dataArray);
        requestAnimationFrame(monitorAudio);
      };
      requestAnimationFrame(monitorAudio);
    } catch (error) {
      cleanupRecordingResources();
      setIsRecording(false);
      window.alert(
        "Unable to start recording. Please allow screen and microphone access."
      );
    }
  }, [
    beginSessionTime,
    cleanupRecordingResources,
    session.resultId,
    triggerRecordingDownload,
    uploadRecordingToBackend
  ]);

  const handleRecordingToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      } else {
        cleanupRecordingResources();
      }
    };
  }, [cleanupRecordingResources]);

  useEffect(() => {
    if (!isRecording) {
      setRecordingElapsedSec(0);
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (!recordingStartedAtRef.current) return;
      const elapsed = Math.floor(
        (Date.now() - recordingStartedAtRef.current) / 1000
      );
      setRecordingElapsedSec(elapsed);
    }, 500);

    return () => clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => {
    const currentTimeEvents = sortedEvents.filter(
      evt => Math.abs(evt.timeSec - sessionTimeSec) <= 0.5
    );

    let targetEvent = currentTimeEvents[0];
    if (!targetEvent) {
      const pastEvents = sortedEvents.filter(
        evt => evt.timeSec <= sessionTimeSec
      );
      targetEvent = pastEvents[pastEvents.length - 1];
    }

    if (!targetEvent) {
      lastAutoScrolledEventIdRef.current = null;
      return;
    }

    if (lastAutoScrolledEventIdRef.current === targetEvent.id) {
      return;
    }

    const rowEl = eventRowRefs.current[targetEvent.id];
    if (rowEl && rowEl.scrollIntoView) {
      rowEl.scrollIntoView({ behavior: "smooth", block: "start" });
      lastAutoScrolledEventIdRef.current = targetEvent.id;
    }
  }, [sortedEvents, sessionTimeSec]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconButton onClick={onBack} color="inherit" size="small">
          <ArrowBack />
        </IconButton>
        <div>
          <Typography variant="h6">
            {`${session.name || session.uniqueId} — Synchronized View`}
          </Typography>
          <Typography variant="caption" className={styles.subtitle}>
            {`${session.platformName} · ${session.testName} · ${
              session.timeCollected
                ? require("moment")
                    .utc(session.timeCollected)
                    .local()
                    .format("MMM Do YYYY, h:mm a")
                : session.dateString
            }`}
          </Typography>
        </div>
      </div>

      <div
        className={styles.videoGrid}
        style={{
          gridTemplateColumns: `repeat(${Math.min(videos.length, 3)}, 1fr)`
        }}
      >
        {videos.map((video, index) => (
          <div key={video.id || index} className={styles.videoWrapper}>
            <div className={styles.videoContainer}>
              <video
                ref={el => {
                  videoRefs.current[index] = el;
                }}
                className={styles.video}
              />
            </div>
            <Typography variant="caption" className={styles.videoLabel}>
              {`${video.type} — ${video.name}`}
            </Typography>
          </div>
        ))}
      </div>

      <div className={styles.controls}>
        <IconButton
          onClick={togglePlayPause}
          color="primary"
          className={styles.playButton}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <button
          type="button"
          className={`${styles.recordButton} ${
            isRecording ? styles.recording : ""
          }`}
          disabled={isUploadingRecording}
          onClick={handleRecordingToggle}
        >
          {isUploadingRecording
            ? "Uploading"
            : isRecording
            ? "Stop Recording"
            : "Record"}
        </button>
        {isRecording && (
          <span className={styles.recordingStatus}>
            <span className={styles.recordingDot} />
            {`Recording ${formatRecordingTime(recordingElapsedSec)}`}
          </span>
        )}
        {isUploadingRecording && (
          <div className={styles.uploadProgressWrap}>
            <span
              className={styles.uploadProgressLabel}
            >{`Upload ${uploadProgressPct}%`}</span>
            <div className={styles.uploadProgressTrack}>
              <div
                className={styles.uploadProgressFill}
                style={{ width: `${uploadProgressPct}%` }}
              />
            </div>
          </div>
        )}
        <span className={styles.timeLabel}>{formatTime(sessionTimeSec)}</span>
        <div className={styles.sliderSection}>
          {/* Slider row with event tick overlays */}
          <div className={styles.sliderWrapper}>
            {duration > 0 &&
              sortedEvents.map(evt => (
                <div
                  key={evt.id}
                  className={styles.eventTick}
                  style={{
                    left: `${Math.min((evt.timeSec / duration) * 100, 100)}%`,
                    borderLeftColor: isEventInActiveSegment(evt)
                      ? ACTIVE_SEGMENT_EVENT_COLOR
                      : PRIORITY_COLORS[evt.priority] || "#1c87e5"
                  }}
                  title={`${evt.name}${
                    evt.description ? `: ${evt.description}` : ""
                  } [${evt.priority}]`}
                />
              ))}
            <input
              type="range"
              className={styles.slider}
              min={0}
              max={duration || 0}
              step={0.1}
              value={sessionTimeSec}
              onInput={handleSliderChange}
              onChange={handleSliderChange}
            />
          </div>

          {/* Segment colour band strip */}
          {segments.length > 0 && duration > 0 && (
            <div className={styles.segmentStrip}>
              {segments.map((seg, i) => (
                <div
                  key={seg.id}
                  className={styles.segmentBand}
                  style={{
                    left: `${Math.min((seg.startSec / duration) * 100, 100)}%`,
                    width: `${Math.min(
                      ((seg.endSec - seg.startSec) / duration) * 100,
                      100
                    )}%`,
                    background: SEGMENT_COLORS[i % SEGMENT_COLORS.length]
                  }}
                  title={`${seg.name}${
                    seg.description ? `: ${seg.description}` : ""
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <span className={styles.timeLabel}>{formatTime(duration)}</span>
      </div>

      {/* Segment legend */}
      {segments.length > 0 && (
        <div className={styles.legend}>
          {segments.map((seg, i) => (
            <span
              key={seg.id}
              className={`${styles.legendItem} ${
                activeSegmentIds.has(seg.id) ? styles.legendItemActive : ""
              }`}
              onClick={() =>
                handleSliderChange({ target: { value: seg.startSec } })
              }
              title={`Seek to ${formatTime(seg.startSec)}`}
            >
              {activeSegmentIds.has(seg.id) && (
                <span className={styles.segmentActiveIndicator} />
              )}
              <span
                className={styles.legendDot}
                style={{
                  background: SEGMENT_COLORS[i % SEGMENT_COLORS.length].replace(
                    "0.4",
                    "0.85"
                  )
                }}
              />
              <span>{seg.name}</span>
              <span className={styles.legendTime}>
                {formatTime(seg.startSec)}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Events list */}
      {events.length > 0 && (
        <div className={styles.eventList}>
          <Typography variant="subtitle2" className={styles.eventListHeader}>
            Events
          </Typography>
          <div className={styles.eventListItems}>
            {sortedEvents.map(evt => (
              <div
                key={evt.id}
                className={styles.eventRow}
                ref={el => {
                  if (el) eventRowRefs.current[evt.id] = el;
                  else delete eventRowRefs.current[evt.id];
                }}
                onClick={() =>
                  handleSliderChange({ target: { value: evt.timeSec } })
                }
              >
                <span
                  className={styles.eventPriorityDot}
                  style={{
                    background: isEventInActiveSegment(evt)
                      ? ACTIVE_SEGMENT_EVENT_COLOR
                      : PRIORITY_COLORS[evt.priority] || "#1c87e5"
                  }}
                />
                <span className={styles.eventTime}>
                  {formatTime(evt.timeSec)}
                </span>
                <span className={styles.eventName}>{evt.name}</span>
                {evt.description && (
                  <span className={styles.eventDesc}>{evt.description}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
